import { Link } from 'react-router-dom';
import { Phone, ShieldCheck, Zap, Award, GraduationCap, Wrench } from 'lucide-react';
import { ContactForm } from '@/components/ContactForm';

const AboutPage = () => {
  const credentials = [
    {
      icon: GraduationCap,
      title: 'Associate in Applied Science',
      subtitle: 'Construction Electricity Management',
      date: 'December 2014',
      institution: 'Florida State College at Jacksonville',
    },
    {
      icon: Award,
      title: 'Technical Certificate',
      subtitle: 'Construction Electricity Assistant',
      date: 'August 2012',
      institution: 'Florida State College at Jacksonville',
    },
  ];

  const values = [
    {
      icon: ShieldCheck,
      title: 'Safety First',
      description: 'Every job follows strict safety protocols to protect you, your property, and our team.',
    },
    {
      icon: Wrench,
      title: 'Quality Workmanship',
      description: 'We take pride in our work. Every installation and repair meets the highest standards.',
    },
    {
      icon: Zap,
      title: 'Reliability',
      description: 'When you call Solid Repair, you get prompt, dependable service you can count on.',
    },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-slate-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/30 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-full mb-6">
              About Us
            </span>
            <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl md:text-6xl font-bold uppercase text-white leading-tight mb-6">
              Meet Solid Repair
              <span className="text-amber-500"> LLC</span>
            </h1>
            <p className="text-xl text-slate-300">
              Professional electrical services built on education, experience, and a commitment to excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section data-testid="story-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wide">
                Our Story
              </span>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase text-slate-900 mt-2 mb-6">
                Electrical Excellence Since Day One
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Solid Repair LLC was founded with a simple mission: to provide high-quality electrical services 
                  that homeowners and businesses can trust. With formal education in Construction Electricity 
                  Management and years of hands-on experience, we bring both knowledge and skill to every project.
                </p>
                <p>
                  We specialize in building, repairing, and maintaining electrical systems across residential, 
                  commercial, and industrial settings. Whether you need a simple outlet repair or a complete 
                  electrical system installation, we approach every job with the same level of professionalism 
                  and attention to detail.
                </p>
                <p>
                  When you choose Solid Repair LLC, you're choosing a contractor who values your safety, 
                  respects your time, and stands behind their work. We're not just fixing wires – we're 
                  building lasting relationships with our clients.
                </p>
              </div>
              
              <div className="mt-8">
                <a
                  href="tel:+19047137611"
                  data-testid="about-call-btn"
                  className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-6 py-3 rounded-lg font-bold transition-all duration-300 hover:scale-105"
                >
                  <Phone className="w-5 h-5" />
                  Call Us Today
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://plus.unsplash.com/premium_photo-1661929137248-2544fd28de13?fm=jpg&q=60&w=800&auto=format&fit=crop"
                alt="Solid Repair LLC Team"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section data-testid="credentials-section" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wide">
              Our Credentials
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-900 mt-2">
              Education & Certifications
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Our expertise is backed by formal education and recognized certifications in electrical systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {credentials.map((cred, index) => (
              <div
                key={cred.title}
                data-testid={`credential-card-${index}`}
                className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:border-amber-500 transition-all duration-300 card-hover"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <cred.icon className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-slate-900 mb-1">
                      {cred.title}
                    </h3>
                    <p className="text-amber-600 font-semibold mb-2">{cred.subtitle}</p>
                    <p className="text-slate-500 text-sm">{cred.institution}</p>
                    <p className="text-slate-400 text-sm">{cred.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-20 md:py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-500 font-semibold text-sm uppercase tracking-wide">
              What We Stand For
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-white mt-2">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-card-${index}`}
                className="text-center"
              >
                <div className="w-16 h-16 bg-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-400">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wide">
                Ready to Get Started?
              </span>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-900 mt-2 mb-6">
                Let's Work Together
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                Whether you have a question or need a quote, we're here to help. 
                Reach out today and let's discuss your electrical needs.
              </p>

              <div className="bg-slate-50 p-6 rounded-xl">
                <h3 className="font-bold text-slate-900 mb-4">Contact Information</h3>
                <div className="space-y-3">
                  <a
                    href="tel:+19047137611"
                    className="flex items-center gap-3 text-lg font-semibold text-slate-700 hover:text-amber-600 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-amber-600" />
                    (904) 713-7611
                  </a>
                  <p className="text-slate-500 text-sm pl-8">
                    Available Monday - Saturday, 7am - 7pm
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl">
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-slate-900 mb-6">
                Send Us a Message
              </h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
